import { BaseObject } from './BaseObject';
import { SVGRenderer } from '../core/SVGRenderer';
import { Point2D } from '../types';

export class Line extends BaseObject {
  public p1: Point2D;
  public p2: Point2D;
  public isHelper: boolean;
  public lineElem: SVGLineElement | null = null;
  public drawProgress: number = 1.0;

  constructor(
    renderer: SVGRenderer,
    p1: Point2D,
    p2: Point2D,
    isHelper: boolean = false,
    id?: string
  ) {
    super(renderer, id);
    this.p1 = { ...p1 };
    this.p2 = { ...p2 };
    this.isHelper = isHelper;
    this.color = isHelper ? renderer.config.helperColor : renderer.config.lineColor;
    this.strokeWidth = isHelper ? 1.5 : 2.5;
    this.render();
  }

  public render(): SVGElement {
    const ext = this.calculateExtendedPoints();

    this.lineElem = this.renderer.createElement('line', {
      id: this.id,
      x1: ext.x1,
      y1: ext.y1,
      x2: ext.x2,
      y2: ext.y2,
      stroke: this.color,
      'stroke-width': this.strokeWidth,
      'stroke-linecap': 'round',
    });

    if (this.isHelper) {
      this.lineElem.setAttribute('stroke-dasharray', '5,5');
      this.renderer.layers.constructions.appendChild(this.lineElem);
    } else {
      this.renderer.layers.objects.appendChild(this.lineElem);
    }

    this.element = this.lineElem;
    return this.lineElem;
  }

  protected calculateExtendedPoints(): { x1: number; y1: number; x2: number; y2: number } {
    const dx = this.p2.x - this.p1.x;
    const dy = this.p2.y - this.p1.y;
    const len = Math.sqrt(dx * dx + dy * dy);

    if (len === 0) return { x1: this.p1.x, y1: this.p1.y, x2: this.p2.x, y2: this.p2.y };

    const uX = dx / len;
    const uY = dy / len;

    // Parametric clipping to SVG viewBox (0..1000, 0..700) with margin
    const margin = 10;
    const minX = margin, maxX = 1000 - margin;
    const minY = margin, maxY = 700 - margin;

    // Find t range where the line stays inside the box
    let tMin = -5000, tMax = 5000;
    if (Math.abs(uX) > 1e-9) {
      const t1 = (minX - this.p1.x) / uX;
      const t2 = (maxX - this.p1.x) / uX;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else {
      // vertical line - check if x is in range
      if (this.p1.x < minX || this.p1.x > maxX) return { x1: this.p1.x, y1: this.p1.y, x2: this.p2.x, y2: this.p2.y };
    }
    if (Math.abs(uY) > 1e-9) {
      const t1 = (minY - this.p1.y) / uY;
      const t2 = (maxY - this.p1.y) / uY;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else {
      if (this.p1.y < minY || this.p1.y > maxY) return { x1: this.p1.x, y1: this.p1.y, x2: this.p2.x, y2: this.p2.y };
    }

    return {
      x1: this.p1.x + uX * tMin,
      y1: this.p1.y + uY * tMin,
      x2: this.p1.x + uX * tMax,
      y2: this.p1.y + uY * tMax,
    };
  }

  public setDrawProgress(progress: number): void {
    this.drawProgress = Math.max(0, Math.min(1, progress));
    this.update();
  }

  public update(): void {
    if (!this.lineElem) return;
    const ext = this.calculateExtendedPoints();

    const currentX2 = ext.x1 + (ext.x2 - ext.x1) * this.drawProgress;
    const currentY2 = ext.y1 + (ext.y2 - ext.y1) * this.drawProgress;

    this.lineElem.setAttribute('x1', String(ext.x1));
    this.lineElem.setAttribute('y1', String(ext.y1));
    this.lineElem.setAttribute('x2', String(currentX2));
    this.lineElem.setAttribute('y2', String(currentY2));
    this.lineElem.setAttribute('stroke', this.color);
  }
}
